import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { CheckoutForm } from '@/components/payment/CheckoutForm'
import { MockCheckoutForm } from '@/components/payment/MockCheckoutForm'
import { api } from '@/lib/api'
import { Loader2, ArrowLeft, CreditCard } from 'lucide-react'
import Logo from '@/components/Logo'

// Check if mock mode is enabled
const MOCK_MODE = import.meta.env.VITE_STRIPE_MOCK_MODE === 'true'

interface OrderSummary {
  id: string
  items: Array<{
    productName: string
    quantity: number
    unitPrice: number
    itemTotal: number
  }>
  subtotal: number
  tax: number
  total: number
}

export function PaymentPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const orderId = searchParams.get('orderId')

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [order, setOrder] = useState<OrderSummary | null>(null)
  const [paymentId, setPaymentId] = useState<string | null>(null)
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null)

  // Customer info
  const [customerName, setCustomerName] = useState('')
  const [customerEmail, setCustomerEmail] = useState('')
  const [customerInfoSubmitted, setCustomerInfoSubmitted] = useState(false)

  useEffect(() => {
    if (!orderId) {
      setError('Order ID non trovato')
      setLoading(false)
      return
    }

    // Load order details
    loadOrder()
  }, [orderId])

  const loadOrder = async () => {
    try {
      const response = await api.get(`/api/public/order/${orderId}`)
      setOrder(response.data)
      setLoading(false)
    } catch (err: any) {
      console.error('Error loading order:', err)
      setError(err.response?.data?.message || 'Errore nel caricamento dell\'ordine')
      setLoading(false)
    }
  }

  const handleSubmitCustomerInfo = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!customerName.trim() || !customerEmail.trim()) {
      setError('Nome ed email sono obbligatori')
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail)) {
      setError('Email non valida')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Create payment intent
      const response = await api.post('/api/payments/public/create-payment-intent', {
        orderId,
        customerEmail,
        customerName
      })

      setClientSecret(response.data.clientSecret)
      setPaymentId(response.data.paymentId)

      // Load Stripe with publishable key from backend
      if (!MOCK_MODE && response.data.publishableKey) {
        const stripe = await loadStripe(response.data.publishableKey)
        setStripePromise(Promise.resolve(stripe))
      }

      setCustomerInfoSubmitted(true)
      setLoading(false)
    } catch (err: any) {
      console.error('Error creating payment intent:', err)
      setError(err.response?.data?.error || 'Errore nella creazione del pagamento')
      setLoading(false)
    }
  }

  if (loading && !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Caricamento...</p>
        </div>
      </div>
    )
  }

  if (error && !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-5xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Errore</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
          >
            Torna alla Home
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Logo size={36} variant="full" />
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition"
          >
            <ArrowLeft size={20} />
            <span>Indietro</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Title */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-6">
            <div className="flex items-center gap-3 mb-2">
              <CreditCard size={32} />
              <h1 className="text-3xl font-bold">Pagamento</h1>
            </div>
            <p className="text-blue-100">Completa il tuo ordine in modo sicuro</p>
          </div>

          <div className="p-8">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Left Column - Order Summary */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Riepilogo Ordine</h2>

                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="space-y-3">
                    {order?.items.map((item, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-gray-700">
                          {item.quantity}x {item.productName}
                        </span>
                        <span className="font-medium text-gray-900">
                          €{item.itemTotal.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-gray-200 mt-4 pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotale</span>
                      <span className="text-gray-900">€{order?.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">IVA (10%)</span>
                      <span className="text-gray-900">€{order?.tax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t border-gray-300 pt-2">
                      <span className="text-gray-900">Totale</span>
                      <span className="text-blue-600">€{order?.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Security Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">🔒</div>
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-1">Pagamento Sicuro</h3>
                      <p className="text-sm text-blue-700">
                        I tuoi dati sono protetti con crittografia SSL.
                        Pagamento elaborato da Stripe.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Payment Form */}
              <div>
                {!customerInfoSubmitted ? (
                  // Step 1: Customer Information
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">I Tuoi Dati</h2>

                    <form onSubmit={handleSubmitCustomerInfo} className="space-y-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                          Nome Completo *
                        </label>
                        <input
                          id="name"
                          type="text"
                          value={customerName}
                          onChange={(e) => setCustomerName(e.target.value)}
                          placeholder="Mario Rossi"
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                          Email *
                        </label>
                        <input
                          id="email"
                          type="email"
                          value={customerEmail}
                          onChange={(e) => setCustomerEmail(e.target.value)}
                          placeholder="mario.rossi@example.com"
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Riceverai la ricevuta a questo indirizzo
                        </p>
                      </div>

                      {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                          {error}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="animate-spin" size={20} />
                            <span>Elaborazione...</span>
                          </>
                        ) : (
                          <>
                            <span>Continua al Pagamento</span>
                          </>
                        )}
                      </button>
                    </form>
                  </div>
                ) : (
                  // Step 2: Payment Details (Stripe or Mock)
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Dettagli Pagamento</h2>

                    {MOCK_MODE ? (
                      // Mock Mode - Use fake checkout form
                      <MockCheckoutForm
                        orderId={orderId!}
                        paymentId={paymentId!}
                        amount={order?.total || 0}
                        customerEmail={customerEmail}
                        customerName={customerName}
                      />
                    ) : (
                      // Real Stripe Mode
                      clientSecret && stripePromise && (
                        <Elements
                          stripe={stripePromise}
                          options={{
                            clientSecret,
                            appearance: {
                              theme: 'stripe',
                              variables: {
                                colorPrimary: '#2563eb',
                                colorBackground: '#ffffff',
                                colorText: '#1f2937',
                                colorDanger: '#dc2626',
                                fontFamily: 'system-ui, sans-serif',
                                spacingUnit: '4px',
                                borderRadius: '8px',
                              },
                            },
                          }}
                        >
                          <CheckoutForm
                            orderId={orderId!}
                            paymentId={paymentId!}
                            amount={order?.total || 0}
                            customerEmail={customerEmail}
                            customerName={customerName}
                          />
                        </Elements>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer Info */}
        <div className="mt-8 text-center text-sm text-gray-600">
          <p>Hai bisogno di aiuto? Contatta il ristorante direttamente.</p>
          <p className="mt-2">Powered by <span className="font-semibold">Qorder</span> & <span className="font-semibold">Stripe</span></p>
        </div>
      </div>
    </div>
  )
}

export default PaymentPage
